import { useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
	UnifiedSearchRequestModel,
	UnifiedSearchResultModel,
	Client as SearchClient,
	MatchType,
} from '@bizzkit/ecommerce-search/client';
import { QueryClient, UseQueryResult, useQuery } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import { queryKeys } from '@/api/apiConfig';
import { mapSearchFilters } from '@/api/appSolutionBuilder/mapping';
import { Filter, FilterType, ProductSort } from '@/api/product/types';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { prefetchGetCustomerSegments } from '@/generated/customer-segment';
import type { SearchSettingsResponse, SegmentationId } from '@/generated/data.schemas';
import { Area, PageType } from '@/generated/data.schemas';
import { prefetchAttributeDescriptions } from './attributes';
import { prefetchAllGlobalLists, prefetchGlobalListOrder } from './globalLists';
import { filterUnitServerSideFetch } from './filterUnit';
import type { AdditionalsPayload, ConfigurationProductsProps } from './types';
import { AppSolutionBuilderStep } from './types';

export interface AppSolutionBuilderPageQuery {
	segmentationId?: number;
	forceStep?: string;
	segment?: string;
	forceSegment?: string;
	application?: string;
	forceApplication?: string;
	subApplication?: string;
	forceSubApplication?: string;
	specifications?: string;
	specificationFilters?: Filter[];
	filterUnit?: string;
	associatedFilterUnit?: string;
	configuration?: string;
	configurationProducts?: ConfigurationProductsProps;
	associatedConfiguration?: string;
	associatedConfigurationProducts?: ConfigurationProductsProps;
	additional?: string;
	associatedAdditional?: string;
	summary?: string;
}

/**
 * URL parameter name shortcuts (keeps URLs short while enum values stay as translation keys):
 * segment        → seg       | associatedFilterUnit     → afu
 * application    → app       | configuration            → cfg
 * subApplication → sub       | associatedConfiguration  → acfg
 * specifications → spec      | inserts                  → ins
 * filterUnit     → fu        | addons                   → ao
 * additional     → add       | associatedInserts        → ains
 * summary        → sum       | associatedAdditional     → aadd
 * forceStep      → fs        | forceSegment             → fseg
 * forceApplication → fapp    | forceSubApplication      → fsub
 */
export const ForceStepName = 'fs';
export const ForceSegmentName = 'fseg';
export const ForceApplicationName = 'fapp';
export const ForceSubApplicationName = 'fsub';
const SegmentName = 'seg';
const ApplicationName = 'app';
const SubApplicationName = 'sub';
const SpecificationsName = 'spec';
const FilterUnitName = 'fu';
const AssociatedFilterUnitName = 'afu';
const ConfigurationName = 'cfg';
const InsertsName = 'ins';
const AddOnsName = 'ao';
const AssociatedConfigurationName = 'acfg';
const AssociatedInsertsName = 'ains';
const ServiceName = 'add';
const AssociatedServiceName = 'aadd';
const SummaryName = 'sum';

export const ValueSeparator = '--';
const FromSuffix = '.from';
const ToSuffix = '.to';
const DefaultSortBy = ProductSort.Score;
const DefaultSortDirection = 'desc'; // SortDirection.Descending;

/** Map URL param name → AppSolutionBuilderStep enum value */
export const ParamToStep: Record<string, AppSolutionBuilderStep> = {
	[SegmentName]: AppSolutionBuilderStep.Segment,
	[ApplicationName]: AppSolutionBuilderStep.Application,
	[SubApplicationName]: AppSolutionBuilderStep.SubApplication,
	[SpecificationsName]: AppSolutionBuilderStep.Specifications,
	[FilterUnitName]: AppSolutionBuilderStep.FilterUnit,
	[ConfigurationName]: AppSolutionBuilderStep.Configuration,
	[ServiceName]: AppSolutionBuilderStep.Additional,
	[SummaryName]: AppSolutionBuilderStep.Summary,
};

/** Map AppSolutionBuilderStep enum value → URL param name */
export const StepToParam: Record<AppSolutionBuilderStep, string> = {
	[AppSolutionBuilderStep.Segment]: SegmentName,
	[AppSolutionBuilderStep.Application]: ApplicationName,
	[AppSolutionBuilderStep.SubApplication]: SubApplicationName,
	[AppSolutionBuilderStep.Specifications]: SpecificationsName,
	[AppSolutionBuilderStep.FilterUnit]: FilterUnitName,
	[AppSolutionBuilderStep.Configuration]: ConfigurationName,
	[AppSolutionBuilderStep.Additional]: ServiceName,
	[AppSolutionBuilderStep.Summary]: SummaryName,
};

const mapSubApplicationArray = (query: AppSolutionBuilderPageQuery): string[] => {
	const subText = query.forceSubApplication ?? query.subApplication;
	if (subText) {
		return subText.split('-').reverse();
	}
	return [];
};

export const searchAppSolutionBuilderProducts = async (
	query: AppSolutionBuilderPageQuery,
	settings?: SearchSettingsResponse,
): Promise<UnifiedSearchResultModel> => {
	if (!settings) throw new Error('Search settings are not available.');
	const searchClient = new SearchClient({ baseUrl: settings.searchApiHost });
	const scopeId = settings.applicationSolutionBuilderScopeId;

	const request: UnifiedSearchRequestModel = {
		segmentId: settings.searchSegment,
		scopeId: scopeId,
		authenticationToken: settings.searchToken,
		offsetProducts: 0,
	};

	const filters = mapSearchFilters(query.specificationFilters);
	if (
		query.application ||
		query.forceApplication ||
		query.segment ||
		query.forceSegment ||
		query.forceSubApplication ||
		query.subApplication
	) {
		const applicationArray: string[] =
			query.forceApplication !== undefined
				? [query.forceApplication]
				: query.application !== undefined
					? [query.application]
					: [];

		const segmentArray =
			query.forceSegment !== undefined
				? [query.forceSegment]
				: query.segment !== undefined
					? [query.segment]
					: [];

		const subApplicationArray: string[] = mapSubApplicationArray(query);

		const values = [...subApplicationArray, ...applicationArray, ...segmentArray];

		filters[settings.categoryIdsFilterId] = { values, match: MatchType.All };
	}

	if (Object.keys(filters).length > 0) {
		request.filters = filters;
	}

	request.sort = [{ key: DefaultSortBy.toLocaleLowerCase(), order: DefaultSortDirection }];

	const result = await searchClient.search.search(request);

	return result.data;
};

export const parseQueryParameters = (
	segmentationId: SegmentationId,
	searchParams: URLSearchParams,
): AppSolutionBuilderPageQuery => {
	const filters = [] as Filter[];
	const predefinedKeys = [
		ForceStepName,
		SegmentName,
		ForceSegmentName,
		ApplicationName,
		ForceApplicationName,
		SubApplicationName,
		ForceSubApplicationName,
		SpecificationsName,
		FilterUnitName,
		AssociatedFilterUnitName,
		ConfigurationName,
		InsertsName,
		AddOnsName,
		AssociatedConfigurationName,
		AssociatedInsertsName,
		ServiceName,
		AssociatedServiceName,
	];

	searchParams.forEach(function (value, key) {
		if (predefinedKeys.some((k) => k === key)) return;

		if (key.endsWith(ToSuffix)) {
			const id = key.replace(ToSuffix, '');
			const match = filters.find((f) => f.id === id);
			if (match) {
				match.max = Number(value);
			} else {
				filters.push({ id, type: FilterType.Number, min: undefined, max: Number(value) });
			}
		} else if (key.endsWith(FromSuffix)) {
			const id = key.replace(FromSuffix, '');
			const match = filters.find((f) => f.id === id);
			if (match) {
				match.min = Number(value);
			} else {
				filters.push({ id, type: FilterType.Number, min: Number(value), max: undefined });
			}
		} else {
			filters.push({
				id: key,
				type: FilterType.String,
				values: value.split(ValueSeparator).map((v) => {
					return { label: key, value: v, isSelected: true, hitCount: 0 };
				}),
			});
		}
	});

	const specificationsValue = searchParams.get(SpecificationsName);
	const configurationValue = searchParams.get(ConfigurationName);
	const configurationProducts: ConfigurationProductsProps = {
		variantId: configurationValue === '' ? '' : configurationValue || undefined,
		inserts: searchParams.get(InsertsName) || undefined,
		addOns: searchParams.get(AddOnsName) || undefined,
	};

	const associatedConfigurationValue = searchParams.get(AssociatedConfigurationName);
	const associatedConfigurationProducts: ConfigurationProductsProps = {
		variantId: associatedConfigurationValue === '' ? '' : associatedConfigurationValue || undefined,
		inserts: searchParams.get(AssociatedInsertsName) || undefined,
	};

	const serviceValue = searchParams.get(ServiceName);
	const associatedServiceValue = searchParams.get(AssociatedServiceName);

	return {
		segmentationId: segmentationId,
		forceStep: searchParams.get(ForceStepName) || undefined,
		segment: searchParams.get(SegmentName) || undefined,
		forceSegment: searchParams.get(ForceSegmentName) || undefined,
		application: searchParams.get(ApplicationName) || undefined,
		forceApplication: searchParams.get(ForceApplicationName) || undefined,
		subApplication: searchParams.get(SubApplicationName) || undefined,
		forceSubApplication: searchParams.get(ForceSubApplicationName) || undefined,
		specifications: specificationsValue === '' ? '' : undefined,
		specificationFilters: filters,
		filterUnit: searchParams.get(FilterUnitName) || undefined,
		associatedFilterUnit: searchParams.get(AssociatedFilterUnitName) || undefined,
		configuration: configurationValue === '' ? '' : configurationValue || undefined,
		configurationProducts: configurationProducts || undefined,
		associatedConfiguration: associatedConfigurationValue === '' ? '' : associatedConfigurationValue || undefined,
		associatedConfigurationProducts: associatedConfigurationProducts || undefined,
		additional: serviceValue === '' ? '' : serviceValue || undefined,
		associatedAdditional: associatedServiceValue === '' ? '' : associatedServiceValue || undefined,
	};
};

export const useAppSolutionBuilderParams = (): {
	query: AppSolutionBuilderPageQuery;
	setSegment: (segment?: string) => void;
	setApplication: (application?: string) => void;
	setSpecifications: (specifications?: string) => void;
	setSpecificationsFilters: (toBeSet?: Filter[], toBeRemoved?: Filter[]) => void;
	setFilterUnit: (filterUnit?: string, associatedFilterUnit?: string) => void;
	setAssociatedFilterUnit: (associatedFilterUnit?: string) => void;
	setConfiguration: (configuration?: ConfigurationProductsProps) => void;
	setAssociatedConfiguration: (configuration?: ConfigurationProductsProps) => void;
	setConfigurationBoth: (primary: ConfigurationProductsProps, associated: ConfigurationProductsProps) => void;
	editAssociatedConfiguration: () => void;
	setAdditional: (additionals?: AdditionalsPayload) => void;
	setAssociatedAdditional: (additionals?: AdditionalsPayload) => void;
	setSubApplication: (subApplication?: string) => void;
} => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { segmentationId } = useContext(SegmentationContext);
	const currentFilter = parseQueryParameters(segmentationId, searchParams);
	const route = { area: Area.StaticPages, pageType: PageType.AppSolutionBuilder };

	// customer segment menu
	const setSegment = (segment?: string): void => {
		setSearchParams(
			(params) => {
				if (segment) {
					params.set(SegmentName, segment);
					params.delete(ForceStepName);
					params.delete(ForceSegmentName);
				} else {
					const oldSegment = params.get(SegmentName);

					const newParams = new URLSearchParams();
					newParams.set(ForceStepName, SegmentName);
					if (oldSegment) newParams.set(SegmentName, oldSegment);

					return newParams;
				}
				return params;
			},
			{ state: route },
		);
	};

	// customer sub application - submenu from segment
	const setSubApplication = (subApplication?: string): void => {
		setSearchParams(
			(params) => {
				if (subApplication) {
					params.set(SubApplicationName, subApplication);
					params.delete(ForceStepName);
					params.delete(ForceSubApplicationName);
				} else {
					// remove filters for application and all after
					const oldSegment = params.get(SegmentName);
					const oldApplication = params.get(ApplicationName);

					const newParams = new URLSearchParams();
					newParams.set(ForceStepName, ApplicationName); // Don't change it to SubApplicationName. We don't want the subApplication step to be shown as an independent step
					if (oldSegment) newParams.set(SegmentName, oldSegment);
					if (oldApplication) newParams.set(ApplicationName, oldApplication);

					return newParams;
				}
				return params;
			},
			{ state: route, preventScrollReset: true },
		);
	};

	// customer application - submenu from segment
	const setApplication = (application?: string): void => {
		setSearchParams(
			(params) => {
				if (application) {
					params.set(ApplicationName, application);
					params.delete(ForceStepName);
					params.delete(ForceApplicationName);
				} else {
					// remove filters for application and all after
					const oldSegment = params.get(SegmentName);
					const oldApplication = params.get(ApplicationName);

					const newParams = new URLSearchParams();
					newParams.set(ForceStepName, ApplicationName);
					if (oldSegment) newParams.set(SegmentName, oldSegment);
					if (oldApplication) newParams.set(ApplicationName, oldApplication);

					return newParams;
				}
				return params;
			},
			{ state: route, preventScrollReset: true },
		);
	};

	const setSpecifications = (specifications?: string): void => {
		setSearchParams(
			(params) => {
				// set specifications in url to go to next step
				// TODO change to bool
				if (typeof specifications === 'string') {
					params.set(SpecificationsName, specifications);
					params.delete(ForceStepName);
				} else {
					// set force step
					params.set(ForceStepName, SpecificationsName);
					// remove next filters
					params.delete(FilterUnitName);
					params.delete(AssociatedFilterUnitName);
					params.delete(ConfigurationName);
					params.delete(InsertsName);
					params.delete(AddOnsName);
					params.delete(AssociatedConfigurationName);
					params.delete(AssociatedInsertsName);
					params.delete(ServiceName);
				}

				return params;
			},
			{ state: route, preventScrollReset: true },
		);
	};
	// handles filters on specifications
	const setSpecificationsFilters = (toBeSet?: Filter[], toBeRemoved?: Filter[]): void => {
		setSearchParams(
			(params) => {
				if (toBeRemoved) {
					toBeRemoved.forEach((filter) => {
						if (filter.type === FilterType.String) {
							const current = params.get(filter.id);
							if (current) {
								const values = current.split(ValueSeparator);
								const newValues = values.filter((v) => !filter.values?.some((f) => f.value === v));
								if (newValues.length > 0) {
									params.set(
										filter.id,
										newValues.reduce((p, v) => `${p}${ValueSeparator}${v}`),
									);
								} else {
									params.delete(filter.id);
								}
							}
						} else if (filter.type === FilterType.Number) {
							params.delete(filter.id + FromSuffix);
							params.delete(filter.id + ToSuffix);
						}
					});
				}
				if (toBeSet) {
					toBeSet.forEach((filter) => {
						const current = params.get(filter.id);
						if (current) {
							if (filter.type === FilterType.String && !!filter.values?.length) {
								params.set(
									filter.id,
									filter.values.map((v) => v.value).reduce((p, v) => `${p}${ValueSeparator}${v}`),
								);
							} else if (filter.type === FilterType.Number) {
								if (filter.min) {
									params.set(filter.id + FromSuffix, filter.min.toString());
								}
								if (filter.max) {
									params.set(filter.id + ToSuffix, filter.max.toString());
								}
							}
						} else {
							if (filter.type === FilterType.String && !!filter.values?.length) {
								params.append(
									filter.id,
									filter.values.map((v) => v.value).reduce((p, v) => `${p}${ValueSeparator}${v}`),
								);
							} else if (filter.type === FilterType.Number) {
								if (filter.min) {
									params.append(filter.id + FromSuffix, filter.min.toString());
								}
								if (filter.max) {
									params.append(filter.id + ToSuffix, filter.max.toString());
								}
							}
						}
					});
				}

				// reset filters
				if (!toBeRemoved && !toBeSet) {
					// remove filters for application and all after

					const oldSegment = params.get(SegmentName);
					const oldApplication = params.get(ApplicationName);
					const oldSubApplication = params.get(SubApplicationName);
					const oldSpecification = params.get(SpecificationsName);

					const oldForceStepName = params.get(ForceStepName);

					const oldForceSegmentName = params.get(ForceSegmentName);
					const oldForceApplicationName = params.get(ForceApplicationName);

					const newParams = new URLSearchParams();
					if (oldSegment) newParams.set(SegmentName, oldSegment);
					if (oldApplication) newParams.set(ApplicationName, oldApplication);
					if (oldSubApplication) newParams.set(SubApplicationName, oldSubApplication);
					if (oldSpecification) newParams.set(SpecificationsName, oldSpecification);

					if (oldForceStepName) newParams.set(ForceStepName, oldForceStepName);

					if (oldForceSegmentName) newParams.set(ForceSegmentName, oldForceSegmentName);
					if (oldForceApplicationName) newParams.set(ForceApplicationName, oldForceApplicationName);

					return newParams;
				}

				return params;
			},
			{ state: route, preventScrollReset: true },
		);
	};

	// (product)
	const setFilterUnit = (filterUnitId?: string, associatedFilterUnitId?: string): void => {
		setSearchParams(
			(params) => {
				if (typeof filterUnitId === 'string') {
					params.set(FilterUnitName, filterUnitId);
					params.delete(ForceStepName);

					// Atomically set or clear associated unit in the same URL update
					if (typeof associatedFilterUnitId === 'string') {
						params.set(AssociatedFilterUnitName, associatedFilterUnitId);
					} else {
						params.delete(AssociatedFilterUnitName);
						params.delete(AssociatedConfigurationName);
						params.delete(AssociatedInsertsName);
						params.delete(AssociatedServiceName);
					}
				} else {
					// set force step
					params.set(ForceStepName, FilterUnitName);
					// remove next filters and associated selection so the user starts fresh
					params.delete(ConfigurationName);
					params.delete(InsertsName);
					params.delete(AddOnsName);
					params.delete(AssociatedFilterUnitName);
					params.delete(AssociatedConfigurationName);
					params.delete(AssociatedInsertsName);
					params.delete(AssociatedServiceName);
					params.delete(ServiceName);
				}
				return params;
			},
			{ state: route, preventScrollReset: true },
		);
	};

	const setAssociatedFilterUnit = (associatedFilterUnitId?: string): void => {
		setSearchParams(
			(params) => {
				if (typeof associatedFilterUnitId === 'string') {
					params.set(AssociatedFilterUnitName, associatedFilterUnitId);
				} else {
					params.delete(AssociatedFilterUnitName);
				}
				return params;
			},
			{ state: route, preventScrollReset: true },
		);
	};

	// addOnIds and inserts (products)  - can be empty if none chosen
	const setConfiguration = (config?: ConfigurationProductsProps): void => {
		setSearchParams(
			(params) => {
				if (config) {
					params.set(ConfigurationName, config.variantId || '');
					params.delete(InsertsName);
					params.delete(AddOnsName);
					if (config.inserts) params.set(InsertsName, config.inserts);
					if (config.addOns) params.set(AddOnsName, config.addOns);
					params.delete(ForceStepName);
				} else {
					// set force step
					params.set(ForceStepName, ConfigurationName);
					params.delete(ServiceName);
					params.delete(AssociatedConfigurationName);
					params.delete(AssociatedInsertsName);
				}
				return params;
			},
			{ state: route, preventScrollReset: true },
		);
	};

	const setAssociatedConfiguration = (config?: ConfigurationProductsProps): void => {
		setSearchParams(
			(params) => {
				if (config) {
					params.set(AssociatedConfigurationName, config.variantId || '');
					params.delete(AssociatedInsertsName);
					if (config.inserts) params.set(AssociatedInsertsName, config.inserts);
				} else {
					params.delete(AssociatedConfigurationName);
					params.delete(AssociatedInsertsName);
				}
				return params;
			},
			{ state: route, preventScrollReset: true },
		);
	};

	// Atomic update for both primary and associated configuration in a single navigation
	const setConfigurationBoth = (
		primary: ConfigurationProductsProps,
		associated: ConfigurationProductsProps,
	): void => {
		setSearchParams(
			(params) => {
				params.set(ConfigurationName, primary.variantId || '');
				params.delete(InsertsName);
				params.delete(AddOnsName);
				if (primary.inserts) params.set(InsertsName, primary.inserts);
				if (primary.addOns) params.set(AddOnsName, primary.addOns);
				params.set(AssociatedConfigurationName, associated.variantId || '');
				params.delete(AssociatedInsertsName);
				if (associated.inserts) params.set(AssociatedInsertsName, associated.inserts);
				params.delete(ForceStepName);
				return params;
			},
			{ state: route, preventScrollReset: true },
		);
	};

	const setAdditional = (additionals?: AdditionalsPayload): void => {
		setSearchParams(
			(params) => {
				if (additionals) {
					const { spareParts, accessories } = additionals;
					let sparePartsString = '';
					let accessoriesString = '';
					if (spareParts) {
						sparePartsString = `spareparts=${spareParts.map((x) => `${x.id}-${x.quantity}`).join(ValueSeparator)}`;
					}
					if (accessories) {
						accessoriesString = `accessories=${accessories.map((x) => `${x.id}-${x.quantity}`).join(ValueSeparator)}`;
					}
					params.set(ServiceName, `${sparePartsString}${accessories ? '---' : ''}${accessoriesString}`);
					params.delete(ForceStepName);
				} else {
					// set force step
					params.set(ForceStepName, ServiceName);
					params.delete(AppSolutionBuilderStep.Summary);
				}
				return params;
			},
			{ state: route },
		);
	};

	const setAssociatedAdditional = (additionals?: AdditionalsPayload): void => {
		setSearchParams(
			(params) => {
				if (additionals) {
					const { spareParts, accessories } = additionals;
					let sparePartsString = '';
					let accessoriesString = '';
					if (spareParts) {
						sparePartsString = `spareparts=${spareParts.map((x) => `${x.id}-${x.quantity}`).join(ValueSeparator)}`;
					}
					if (accessories) {
						accessoriesString = `accessories=${accessories.map((x) => `${x.id}-${x.quantity}`).join(ValueSeparator)}`;
					}
					params.set(
						AssociatedServiceName,
						`${sparePartsString}${accessories ? '---' : ''}${accessoriesString}`,
					);
				} else {
					params.delete(AssociatedServiceName);
				}
				return params;
			},
			{ state: route },
		);
	};

	// Force-navigate to Configuration in associated phase, keeping associated config for pre-selection
	const editAssociatedConfiguration = (): void => {
		setSearchParams(
			(params) => {
				params.set(ForceStepName, ConfigurationName);
				params.delete(ServiceName);
				params.delete(AssociatedServiceName);
				return params;
			},
			{ state: route, preventScrollReset: true },
		);
	};

	return {
		query: currentFilter,
		setSegment,
		setApplication,
		setSubApplication,
		setSpecifications,
		setSpecificationsFilters,
		setFilterUnit,
		setAssociatedFilterUnit,
		setConfiguration,
		setAssociatedConfiguration,
		setConfigurationBoth,
		editAssociatedConfiguration,
		setAdditional,
		setAssociatedAdditional,
	};
};

export const appSolutionBuildServerSideFetch = async (
	queryClient: QueryClient,
	segmentationId: number,
	pageOrigin: string,
	queryString?: string,
	settings?: SearchSettingsResponse,
	axiosRequestConfig?: AxiosRequestConfig,
): Promise<void> => {
	const searchParams = new URLSearchParams(queryString);
	const query = parseQueryParameters(segmentationId, searchParams);
	const { segment, application, subApplication, specificationFilters, filterUnit } = query;

	// get customer segments
	await prefetchGetCustomerSegments(queryClient, segmentationId, { axios: axiosRequestConfig });

	// if filter unit in query search to get filter unit (product)
	if (filterUnit && settings) {
		await filterUnitServerSideFetch(queryClient, filterUnit || '', settings, pageOrigin);
	}

	const searchQueryKey = queryKeys.appSolutionBuilder.search(
		segmentationId,
		segment,
		application,
		subApplication,
		specificationFilters,
		settings,
	).queryKey;

	await queryClient.prefetchQuery({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: searchQueryKey,
		queryFn: () => searchAppSolutionBuilderProducts(query, settings),
	});

	const searchResult = queryClient.getQueryData<UnifiedSearchResultModel>(searchQueryKey);
	const facetIds = searchResult?.facets?.map((f) => f.key).filter((k): k is string => !!k) ?? [];
	await Promise.all([
		prefetchAllGlobalLists(queryClient, segmentationId, axiosRequestConfig),
		prefetchAttributeDescriptions(queryClient, segmentationId, axiosRequestConfig),
		...facetIds.map((id) => prefetchGlobalListOrder(queryClient, segmentationId, id, axiosRequestConfig)),
	]);
};

export const useAppSolutionBuilderSearchQuery = (
	query: AppSolutionBuilderPageQuery,
	settings?: SearchSettingsResponse,
	isActive?: boolean,
): UseQueryResult<UnifiedSearchResultModel> => {
	const {
		segmentationId,
		segment,
		forceSegment,
		application,
		subApplication,
		forceApplication,
		forceSubApplication,
		specificationFilters,
	} = query;

	// force segment and application overwrites from locations
	const segmentValue = forceSegment || segment;
	const applicationValue = forceApplication || application;
	const subApplicationValue = forceSubApplication || subApplication;

	return useQuery({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: queryKeys.appSolutionBuilder.search(
			segmentationId,
			segmentValue,
			applicationValue,
			subApplicationValue,
			specificationFilters,
			settings,
		).queryKey,
		queryFn: () => searchAppSolutionBuilderProducts(query, settings),
		enabled: isActive,
		retry: false,
	});
};
